import { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RestoreIcon from '@mui/icons-material/Restore'
import UndoIcon from '@mui/icons-material/Undo'
import PublishIcon from '@mui/icons-material/Publish'
import CompareIcon from '@mui/icons-material/Compare'
import VisibilityIcon from '@mui/icons-material/Visibility'
import type { WebsitePreviewDevice } from './types'
import { WebsitePreviewFrame } from './WebsitePreviewFrame'
import { WebsiteBuilderService, type WebsiteRevisionComparison, type WebsiteRevisionRead, type WebsiteRevisionSummary } from './WebsiteBuilderService'
import { revisionActionState } from './revisionActions'

type HistoryScreen = { kind: 'list' } | { kind: 'preview'; revision: WebsiteRevisionRead } | { kind: 'compare'; from: WebsiteRevisionRead; to: WebsiteRevisionRead; facts: WebsiteRevisionComparison }

export interface RevisionHistoryPanelProps {
  open: boolean
  sessionId: string
  siteId: string
  currentRevision: number
  service: WebsiteBuilderService
  device?: WebsitePreviewDevice
  devMode?: boolean
  publishing?: boolean
  onClose: () => void
  onRequestFullscreen?: (fullscreen: boolean) => void
  onRevisionCreated?: (result: { revision: number; html?: string; renderHtml?: string }) => void
  /** Publishing remains Reachy-owned; this callback must publish only currentRevision. */
  onPublishCurrent: (revision: number) => void | Promise<void>
}

function timeLabel(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function operationId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`
}

/** Revisions remain zero-based internally; the product presents them as one-based versions. */
function displayVersion(revision: number): number {
  return revision + 1
}

export function RevisionHistoryPanel({ open, sessionId, siteId, currentRevision, service, device = 'desktop', devMode = false, publishing = false, onClose, onRequestFullscreen, onRevisionCreated, onPublishCurrent }: RevisionHistoryPanelProps) {
  const [revisions, setRevisions] = useState<WebsiteRevisionSummary[] | null>(null)
  const [screen, setScreen] = useState<HistoryScreen>({ kind: 'list' })
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [restoreCandidate, setRestoreCandidate] = useState<WebsiteRevisionSummary | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try { setRevisions(await service.listRevisions(sessionId, { limit: 100 })) }
    catch { setError('Could not load version history.') }
  }, [service, sessionId])

  useEffect(() => { if (open) void load() }, [open, load, currentRevision])
  useEffect(() => { if (!open) { setScreen({ kind: 'list' }); onRequestFullscreen?.(false) } }, [open, onRequestFullscreen])

  const enterScreen = (next: HistoryScreen) => { setScreen(next); onRequestFullscreen?.(next.kind !== 'list') }
  const preview = async (revision: number) => {
    setBusy(`view-${revision}`); setError(null)
    try { enterScreen({ kind: 'preview', revision: await service.readRevision(sessionId, revision) }) }
    catch { setError(`Could not load version ${displayVersion(revision)}.`) }
    finally { setBusy(null) }
  }
  const compare = async (revision: WebsiteRevisionSummary) => {
    const other = revision.revision === currentRevision ? revision.parentRevision : currentRevision
    if (other === null) return
    const fromRevision = Math.min(revision.revision, other), toRevision = Math.max(revision.revision, other)
    setBusy(`compare-${revision.revision}`); setError(null)
    try {
      const [from, to, facts] = await Promise.all([
        service.readRevision(sessionId, fromRevision), service.readRevision(sessionId, toRevision),
        service.compareRevisions(sessionId, fromRevision, toRevision),
      ])
      enterScreen({ kind: 'compare', from, to, facts })
    } catch { setError('Could not compare these versions.') }
    finally { setBusy(null) }
  }
  const restore = async (revision: WebsiteRevisionSummary) => {
    setBusy(`restore-${revision.revision}`); setError(null)
    try {
      const result = await service.restoreRevision(sessionId, revision.revision, currentRevision, operationId('restore'))
      if (result.stale) { setError('The website changed before it could be restored. History has been refreshed.'); await load(); return }
      if (result.mutated) { onRevisionCreated?.({ revision: result.revision, html: result.html, renderHtml: result.renderHtml }); await load() }
      setRestoreCandidate(null)
    } catch { setError(`Could not restore version ${displayVersion(revision.revision)}.`) }
    finally { setBusy(null) }
  }
  const undo = async () => {
    setBusy('undo'); setError(null)
    try {
      const result = await service.undoRevision(sessionId, currentRevision, operationId('undo'))
      if (result.stale) { setError('The website changed before it could be undone. History has been refreshed.'); await load(); return }
      if (result.mutated) { onRevisionCreated?.({ revision: result.revision, html: result.html, renderHtml: result.renderHtml }); await load() }
    } catch { setError('Could not undo the latest change.') }
    finally { setBusy(null) }
  }

  const head = useMemo(() => revisions?.find(revision => revision.revision === currentRevision), [revisions, currentRevision])
  const currentPublishedVersion = head?.publishedVersions?.length ? Math.max(...head.publishedVersions) : null
  if (!open) return null

  if (screen.kind !== 'list') {
    const back = () => enterScreen({ kind: 'list' })
    return (
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 6, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tooltip title="Back to version history"><IconButton size="small" onClick={back} sx={{ color: 'text.secondary' }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Typography sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: 'primary.dark', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {screen.kind === 'preview' ? `Version ${displayVersion(screen.revision.revision)} · Read-only` : `Version ${displayVersion(screen.from.revision)} → ${displayVersion(screen.to.revision)}`}
          </Typography>
          {screen.kind === 'preview' && screen.revision.revision !== currentRevision && <Button size="small" startIcon={<RestoreIcon />} onClick={() => { enterScreen({ kind: 'list' }); setRestoreCandidate(screen.revision) }}>Restore</Button>}
        </Box>
        {screen.kind === 'preview' ? (
          <Box sx={{ flex: 1, minHeight: 0 }}><WebsitePreviewFrame html={screen.revision.renderHtml ?? screen.revision.html} siteId={siteId} revision={screen.revision.revision} device={device} readOnly /></Box>
        ) : (
          <>
            <Box sx={{ px: 2, py: 0.75, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
              {screen.facts.changedSections.length > 0 && <Chip size="small" label={`${screen.facts.changedSections.length} section changes`} />}
              {screen.facts.siteChromeChanged && <Chip size="small" label="Header/footer changed" />}
              {screen.facts.layoutChanged && <Chip size="small" label="Layout changed" />}
              {screen.facts.assetsChanged && <Chip size="small" label="Media changed" />}
              {!screen.facts.changed && <Chip size="small" label="No content difference" />}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
              <Box sx={{ width: '50%', borderRight: 1, borderColor: 'divider' }}><WebsitePreviewFrame html={screen.from.renderHtml ?? screen.facts.fromPreview?.renderHtml ?? screen.from.html} siteId={siteId} revision={screen.from.revision} device={device} readOnly title={`Version ${displayVersion(screen.from.revision)}`} /></Box>
              <Box sx={{ width: '50%' }}><WebsitePreviewFrame html={screen.to.renderHtml ?? screen.facts.toPreview?.renderHtml ?? screen.to.html} siteId={siteId} revision={screen.to.revision} device={device} readOnly title={`Version ${displayVersion(screen.to.revision)}`} /></Box>
            </Box>
          </>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 360, maxWidth: '92%', bgcolor: 'background.paper', borderLeft: 1, borderColor: 'divider', boxShadow: 4, display: 'flex', flexDirection: 'column', zIndex: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, flex: 1 }}>Version history</Typography>
        <IconButton size="small" aria-label="Close version history" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <Box sx={{ px: 1.75, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Current version</Typography>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Version {displayVersion(currentRevision)}</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" variant="outlined" startIcon={<UndoIcon />} disabled={!head || !revisionActionState(head, currentRevision, !!busy).canUndo} onClick={() => void undo()}>Undo latest</Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<PublishIcon />}
            disabled={!head || publishing || currentPublishedVersion !== null || !revisionActionState(head, currentRevision, !!busy).canPublish}
            onClick={() => void onPublishCurrent(currentRevision)}
          >
            {publishing ? 'Publishing…' : currentPublishedVersion !== null ? `Published version ${currentPublishedVersion}` : 'Publish'}
          </Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}
      {!revisions && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={20} /></Box>}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {revisions?.map((revision, index) => {
          const actions = revisionActionState(revision, currentRevision, !!busy)
          return (
            <Box key={revision.revision} sx={{ px: 1.75, py: 1.25, bgcolor: revision.revision === currentRevision ? 'action.hover' : 'transparent' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>Version {displayVersion(revision.revision)}</Typography>
                {revision.revision === currentRevision && <Chip size="small" color="primary" variant="outlined" label="Current" sx={{ height: 20, fontSize: '0.62rem' }} />}
                {revision.publishedVersions?.map(version => <Chip key={version} size="small" label={`Published version ${version}`} sx={{ height: 20, fontSize: '0.62rem' }} />)}
                <Typography sx={{ ml: 'auto', fontSize: '0.65rem', color: 'text.secondary' }}>{timeLabel(revision.createdAt)}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4, mt: 0.5 }}>{revision.summary}</Typography>
              {revision.restoreSourceRevision !== null && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>From version {displayVersion(revision.restoreSourceRevision)}</Typography>}
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
                <Button size="small" startIcon={<VisibilityIcon />} disabled={!actions.canView} onClick={() => void preview(revision.revision)}>View</Button>
                <Button size="small" startIcon={<CompareIcon />} disabled={!actions.canCompare} onClick={() => void compare(revision)}>Compare</Button>
                {!actions.canPublish && <Button size="small" startIcon={<RestoreIcon />} disabled={!actions.canRestore} onClick={() => setRestoreCandidate(revision)}>Restore</Button>}
                {busy?.endsWith(String(revision.revision)) && <CircularProgress size={14} sx={{ alignSelf: 'center' }} />}
              </Box>
              {devMode && revision.modelProvenance?.length ? (
                <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 28, px: 0, '& .MuiAccordionSummary-content': { my: 0 } }}><Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{revision.modelProvenance.length === 1 ? 'Model provenance' : `${revision.modelProvenance.length} model contributions`}</Typography></AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>{revision.modelProvenance.map(model => (
                    <Box key={model.attemptId} sx={{ mb: 0.75 }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary' }}>{model.label} · {model.role === 'refine' ? 'Refined' : 'Edited'}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', overflowWrap: 'anywhere' }}>{model.spec}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{model.outputTokens.toLocaleString()} output tokens · {model.maxOutputTokens.toLocaleString()} limit</Typography>
                    </Box>
                  ))}</AccordionDetails>
                </Accordion>
              ) : null}
              {index < revisions.length - 1 && <Divider sx={{ mt: 1.25 }} />}
            </Box>
          )
        })}
      </Box>
      <Dialog open={!!restoreCandidate} onClose={() => setRestoreCandidate(null)}>
        <DialogTitle>Restore version {restoreCandidate ? displayVersion(restoreCandidate.revision) : ''}?</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: '0.85rem' }}>This keeps every existing version and creates version {displayVersion(currentRevision) + 1} from the selected state.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setRestoreCandidate(null)}>Cancel</Button><Button variant="contained" disabled={!!busy} onClick={() => { if (restoreCandidate) void restore(restoreCandidate) }}>Restore as new version</Button></DialogActions>
      </Dialog>
    </Box>
  )
}
